import { notifications } from "@mantine/notifications";
import { X } from "lucide-react";
import { ProductAdminRequestDTO } from "Interface/Admin/Products/admin-product.interface";
import React, { useEffect, useState } from "react";
import { useGetConfigurationByPlantQuery } from "Services/Admin/ConfigurationApiSlice";
import { useLazyGenerateProductCodeQuery } from "Services/Admin/ProductAdminApiSlice";

const PLANT = process.env.REACT_APP_PLANT || "";

interface BasicInfoSectionProps {
  formData: ProductAdminRequestDTO;
  setFormData: React.Dispatch<React.SetStateAction<ProductAdminRequestDTO>>;
  mode: "add" | "edit" | "view";
  open: boolean;
  toggleSection: () => void;
  mainImageFile: File | null;
  setMainImageFile: React.Dispatch<React.SetStateAction<File | null>>;
  addImageFiles: (File | null)[];
  setAddImageFiles: React.Dispatch<React.SetStateAction<(File | null)[]>>;
  addImgsLineNo: number[];
  setAddImgsLineNo: React.Dispatch<React.SetStateAction<number[]>>;
  existingMainImage?: string;
  existingAdditionalImages?: string[];
  onDeleteMainImage?: () => Promise<void>;
  onDeleteAdditionalImage?: (index: number) => Promise<void>;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  formData,
  setFormData,
  mode,
  open,
  toggleSection,
  mainImageFile,
  setMainImageFile,
  addImageFiles,
  setAddImageFiles,
  addImgsLineNo,
  setAddImgsLineNo,
  existingMainImage,
  existingAdditionalImages,
  onDeleteMainImage,
  onDeleteAdditionalImage,
}) => {
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [addImagePreviews, setAddImagePreviews] = useState<(string | null)[]>(
    Array(5).fill(null),
  );
  const [isDeletingMainImage, setIsDeletingMainImage] = useState(false);
  const [isDeletingAdditional, setIsDeletingAdditional] = useState<boolean[]>(
    Array(5).fill(false),
  );

  const handleDeleteMainImageClick = async () => {
    // If a new file was selected but not yet saved, just clear it
    if (mainImageFile) {
      setMainImageFile(null);
      setMainImagePreview(existingMainImage ?? null);
      return;
    }

    if (!onDeleteMainImage) return;

    try {
      setIsDeletingMainImage(true);
      await onDeleteMainImage();
      setMainImagePreview(null);
    } catch (error) {
      console.error("Failed to delete main image", error);
    } finally {
      setIsDeletingMainImage(false);
    }
  };

  const handleDeleteAdditionalImageClick = async (index: number) => {
    // If a new file was selected for this slot, just clear it
    if (addImageFiles[index]) {
      const updatedFiles = [...addImageFiles];
      const updatedPreviews = [...addImagePreviews];
      updatedFiles[index] = null;
      updatedPreviews[index] = existingAdditionalImages?.[index] ?? null;
      setAddImageFiles(updatedFiles);
      setAddImagePreviews(updatedPreviews);
      return;
    }

    if (!onDeleteAdditionalImage) return;

    try {
      setIsDeletingAdditional((prev) => {
        const updated = [...prev];
        updated[index] = true;
        return updated;
      });
      await onDeleteAdditionalImage(index);
    } catch (error) {
      console.error("Failed to delete additional image", error);
    } finally {
      setIsDeletingAdditional((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const { data: config } = useGetConfigurationByPlantQuery(PLANT);
  const [triggerGenerate, { isLoading: isGeneratingCode }] =
    useLazyGenerateProductCodeQuery();

  const showAuthor = config?.isAuthor === 1;
  const [isbnError, setIsbnError] = useState("");
  const [hasGeneratedCode, setHasGeneratedCode] = useState(false);

  useEffect(() => {
    if (mode === "add" && !formData.productCode && !hasGeneratedCode) {
      const generateInitialCode = async () => {
        try {
          const result = await triggerGenerate().unwrap();
          if (result) {
            setFormData((prev) => ({ ...prev, productCode: result }));
            setHasGeneratedCode(true);
          }
        } catch (error) {
          console.error("Failed to generate product code:", error);
        }
      };

      generateInitialCode();
    }
  }, [
    mode,
    formData.productCode,
    hasGeneratedCode,
    setFormData,
    triggerGenerate,
  ]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "isbn") {
      const cleaned = value.replace(/[^0-9]/g, "");
      validateIsbn(cleaned);

      setFormData((prev) => ({ ...prev, isbn: cleaned }));
      return;
    }

    if (name === "productCondition") {
      if (value === "New") {
        setFormData((prev) => ({ ...prev, preownedCondition: "" }));
      }
    }

    let maxLengths: Record<string, number> = {
      productName: 500,
      remark1: 100,
      remarkTwo: 1000,
    };
    if (maxLengths[name] && value.length > maxLengths[name]) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    let value = e.target.value;
    if (value.length > 100) value = value.slice(0, 100);

    const lines = value.split("\n");
    let start = 0;
    let end = lines.length - 1;

    while (start <= end && lines[start] === "") start++;
    while (end >= start && lines[end] === "") end--;

    const descriptions = lines.slice(start, end + 1);
    setFormData((prev) => ({ ...prev, productDescription: descriptions }));
  };

  const validateFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.[0]) {
        const file = e.target.files[0];
        if (!validateFileSize(file)) {
          e.target.value = "";
          return;
        }
        const updatedImageFiles = [...addImageFiles];
        updatedImageFiles[index] = file;
        setAddImageFiles(updatedImageFiles);

        const reader = new FileReader();
        reader.onloadend = () => {
          const updatedPreviews = [...addImagePreviews];
          updatedPreviews[index] = reader.result as string;
          setAddImagePreviews(updatedPreviews);
        };
        reader.readAsDataURL(file);

        if (!existingAdditionalImages?.[index]) {
          setAddImgsLineNo((prev) => [...prev, index]);
        } else {
          setAddImgsLineNo((prev) =>
            prev.includes(index) ? prev : [...prev, index],
          );
        }
      }
    };

  const validateIsbn = (value: string) => {
    if (!value) {
      setIsbnError("");
      return true;
    }

    const numericOnly = /^\d+$/;
    if (!numericOnly.test(value)) {
      setIsbnError("ISBN must contain only numbers");
      return false;
    }

    if (value.length !== 10 && value.length !== 13) {
      setIsbnError("ISBN must be exactly 10 or 13 digits");
      return false;
    }

    setIsbnError("");
    return true;
  };

  return (
    <div className="font-body tracking-wider border rounded-md">
      <button
        type="button"
        onClick={toggleSection}
        className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-50"
      >
        <h3 className="text-lg font-medium">Basic Information</h3>
        {open ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-chevron-up h-5 w-5"
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-chevron-down h-5 w-5"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {mode !== "add" && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Product Code *
                <span className="text-xs">(This field cannot be edited)</span>
              </label>

              <div className="relative">
                <input
                  type="text"
                  name="productCode"
                  value={formData.productCode || ""}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-gray-100 cursor-not-allowed"
                  readOnly
                  disabled
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Product Name *
            </label>
            <input
              type="text"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm"
              readOnly={mode === "view"}
              maxLength={500}
              placeholder="Enter product name (max 500 chars)"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Product Descriptions
            </label>
            <textarea
              name="productDescription"
              value={formData.productDescription || ""}
              onChange={handleDescriptionChange}
              className="w-full px-3 py-2 border rounded-md text-sm h-32"
              readOnly={mode === "view"}
              maxLength={100}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {Array.isArray(formData.productDescription)
                ? (formData.productDescription as string[]).join("\n").length
                : String(formData.productDescription).length}
              /500 characters
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Product Detailed Descriptions
            </label>
            <textarea
              name="remarkTwo"
              value={formData.remarkTwo}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm"
              readOnly={mode === "view"}
              maxLength={1000}
              placeholder="Enter remark (max 1000 chars)"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {(formData.remarkTwo || "").length}/1000 characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              ISBN
            </label>
            <input
              type="text"
              name="isbn"
              value={formData.isbn || ""}
              onChange={handleChange}
              inputMode="numeric"
              maxLength={13}
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                isbnError ? "border-red-500" : ""
              }`}
              readOnly={mode === "view"}
              placeholder="Enter ISBN number"
            />

            {isbnError && (
              <p className="text-red-500 text-xs mt-1">{isbnError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Product Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm"
              readOnly={mode === "view"}
              maxLength={100}
              placeholder="Enter product title"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {(formData.title || "").length}/100 characters
            </div>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Format
            </label>
            <select
              name="formatType"
              value={formData.formatType || ""}
              onChange={handleChange}
              disabled={mode === "view"}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Select format</option>
              <option value="HARDCOVER">Hardcover</option>
              <option value="PAPERBACK">Paperback</option>
            </select>
          </div> */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Product Condition
            </label>
            <select
              name="productCondition"
              value={formData.productCondition || ""}
              onChange={handleChange}
              disabled={mode === "view"}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Select condition</option>
              <option value="New">New</option>
              <option value="Pre-Owned">Pre-Owned</option>
            </select>
          </div>
          {formData.productCondition === "Pre-Owned" ? (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Preowned Condition
              </label>
              <select
                name="preownedCondition"
                value={formData.preownedCondition || ""}
                onChange={handleChange}
                disabled={mode === "view"}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">Select condition</option>
                <option value="Good">Good</option>
                <option value="Almost New">Almost New</option>
              </select>
            </div>
          ) : (
            <></>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Main Product Image
            </label>
            {mode !== "view" && (
              <input
                type="file"
                accept="image/*"
                onChange={handleMainImageChange}
                className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                // required={
                //   mode === "add" || (mode === "edit" && !existingMainImage)
                // }
              />
            )}

            {(mainImagePreview || existingMainImage) && (
              <div className="mt-2 relative inline-block group">
                <img
                  src={mainImagePreview || existingMainImage || undefined}
                  alt="Category preview"
                  className="max-h-40 object-contain border rounded"
                  width="auto"
                  height="auto"
                />

                {mode !== "view" && (
                  <button
                    type="button"
                    onClick={handleDeleteMainImageClick}
                    disabled={isDeletingMainImage}
                    className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Delete main image"
                  >
                    {isDeletingMainImage ? (
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                )}

                {mode === "edit" && existingMainImage && !mainImagePreview && (
                  <p className="text-sm text-gray-500 mt-1">
                    Current uploaded image
                  </p>
                )}
              </div>
            )}
          </div>

          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index}>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Additional Product Image {index + 1}
              </label>

              {mode !== "view" && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAdditionalImagesChange(index)}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                />
              )}

              {(addImagePreviews[index] ||
                existingAdditionalImages?.[index]) && (
                <div className="mt-2 relative inline-block group">
                  <img
                    src={
                      addImagePreviews[index] ||
                      existingAdditionalImages?.[index] ||
                      undefined
                    }
                    alt="Category preview"
                    className="max-h-40 object-contain border rounded"
                    width="auto"
                    height="auto"
                  />
                  {mode !== "view" && (
                    <button
                      type="button"
                      onClick={() => handleDeleteAdditionalImageClick(index)}
                      disabled={isDeletingAdditional[index]}
                      className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      title="Delete additional image"
                    >
                      {isDeletingAdditional[index] ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <X size={16} />
                      )}
                    </button>
                  )}
                  {mode === "edit" &&
                    existingAdditionalImages?.[index] &&
                    !addImagePreviews[index] && (
                      <p className="text-sm text-gray-500 mt-1">
                        Current uploaded image
                      </p>
                    )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
